// Pannello di configurazione integrazione Calendar (Google/Outlook)
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { CredentialsNote } from './CredentialsNote'
import { OutlookSetupWizard } from './OutlookSetupWizard'
import api from '../../../lib/api'

const schema = z.object({
  defaultReminderMinutes: z.coerce.number().int().min(0).max(10080).default(60),
  syncOnTicketAssign: z.boolean().default(true),
  syncOnTicketReschedule: z.boolean().default(true),
})

type FormValues = z.infer<typeof schema>

interface CalendarConfigProps {
  currentMetadata?: Record<string, unknown>
  onSaved?: () => void
}

export function CalendarConfig({ currentMetadata, onSaved }: CalendarConfigProps) {
  const qc = useQueryClient()
  const [connectingProvider, setConnectingProvider] = useState<'google' | 'outlook' | null>(null)
  const [showOutlookWizard, setShowOutlookWizard] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)

  const { register, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      defaultReminderMinutes: (currentMetadata?.['defaultReminderMinutes'] as number) ?? 60,
      syncOnTicketAssign: (currentMetadata?.['syncOnTicketAssign'] as boolean) ?? true,
      syncOnTicketReschedule: (currentMetadata?.['syncOnTicketReschedule'] as boolean) ?? true,
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      await api.put('/integrations/calendar/config', { metadata: data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations'] })
      onSaved?.()
    },
  })

  const connectGoogle = async () => {
    setGoogleError(null)
    setConnectingProvider('google')
    try {
      const res = await api.get<{ authUrl: string }>('/integrations/calendar/google/auth')
      window.open(res.data.authUrl, '_blank', 'width=600,height=700')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore sconosciuto'
      setGoogleError(
        msg.includes('non configurato') || msg.includes('500')
          ? 'Google Calendar non configurato. Imposta GOOGLE_CALENDAR_CLIENT_ID e GOOGLE_CALENDAR_CLIENT_SECRET nelle variabili d\'ambiente del server.'
          : msg
      )
    } finally {
      setConnectingProvider(null)
    }
  }

  if (showOutlookWizard) {
    return (
      <OutlookSetupWizard
        onClose={() => setShowOutlookWizard(false)}
        onConnected={() => {
          setShowOutlookWizard(false)
          void qc.invalidateQueries({ queryKey: ['integrations'] })
          onSaved?.()
        }}
      />
    )
  }

  return (
    <div className="space-y-5">
      <CredentialsNote
        envVars={[
          'GOOGLE_CALENDAR_CLIENT_ID',
          'GOOGLE_CALENDAR_CLIENT_SECRET',
          'OUTLOOK_CLIENT_ID',
          'OUTLOOK_CLIENT_SECRET',
        ]}
      />

      {/* Connessione OAuth2 per provider */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-[var(--fc-foreground)]">
          Connessione account calendario (OAuth2)
        </p>
        <p className="text-xs text-[var(--fc-foreground-muted)]">
          Ogni tecnico deve autorizzare l'accesso al proprio calendario. Usare i pulsanti sottostanti
          per avviare il flusso OAuth2 per il proprio account.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ExternalLink className="h-3 w-3" />}
            onClick={connectGoogle}
            isLoading={connectingProvider === 'google'}
          >
            Connetti Google Calendar
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ExternalLink className="h-3 w-3" />}
            onClick={() => setShowOutlookWizard(true)}
          >
            Configura Outlook →
          </Button>
        </div>
        {googleError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {googleError}
          </p>
        )}
      </div>

      {/* Impostazioni */}
      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
            Promemoria di default (minuti prima dell'evento)
          </label>
          <input
            type="number"
            min={0}
            max={10080}
            className="w-full rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)]"
            {...register('defaultReminderMinutes')}
          />
          <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
            0 = nessun promemoria. 60 = 1 ora prima. 1440 = 1 giorno prima.
          </p>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-[var(--fc-foreground)] mb-2">
            Sincronizzazione automatica
          </legend>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="syncOnTicketAssign"
                className="h-4 w-4 rounded border-[var(--fc-border)]"
                {...register('syncOnTicketAssign')}
              />
              <label htmlFor="syncOnTicketAssign" className="text-sm text-[var(--fc-foreground)]">
                Crea evento al momento dell'assegnazione ticket
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="syncOnTicketReschedule"
                className="h-4 w-4 rounded border-[var(--fc-border)]"
                {...register('syncOnTicketReschedule')}
              />
              <label htmlFor="syncOnTicketReschedule" className="text-sm text-[var(--fc-foreground)]">
                Aggiorna evento al cambio data/ora ticket
              </label>
            </div>
          </div>
        </fieldset>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onSaved}>
            Annulla
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending}>
            Salva configurazione
          </Button>
        </div>
      </form>
    </div>
  )
}
